// OpenAI Responses API adapter (`/v1/responses`). Some Go models (Muse Spark,
// Grok, GPT Luna) are served here instead of `/v1/chat/completions`. Shape
// follows the public Responses API; verified against mocks only until budget
// allows a live run.
import { chatLogHeaders, encodeBody, PROVIDER_BASE_URLS, ssePayloads, streamWithRetry } from './providers.js'
import { parseToolArguments } from './toolArguments.js'

const toResponsesTool = (tool) => ({
  type: 'function',
  name: tool.name,
  description: tool.description,
  parameters: tool.inputSchema,
})

// Exported for tests only; not part of the package's public surface.
export const toResponsesInput = (messages) => {
  const input = []
  let images = []
  const flushImages = () => {
    if (images.length === 0) return
    input.push({ role: 'user', content: images.map((image) => ({ type: 'input_image', image_url: image.dataUrl })) })
    images = []
  }
  for (const message of messages) {
    if (message.role === 'tool') {
      input.push({ type: 'function_call_output', call_id: message.toolCallId, output: message.content })
      if (Array.isArray(message.images)) images.push(...message.images)
      continue
    }
    flushImages()
    if (message.role === 'assistant') {
      if (message.content) input.push({ role: 'assistant', content: message.content })
      for (const call of message.toolCalls ?? []) {
        input.push({ type: 'function_call', call_id: call.id, name: call.name, arguments: JSON.stringify(call.input) })
      }
    } else if (message.role === 'user' && Array.isArray(message.images) && message.images.length > 0) {
      input.push({
        role: 'user',
        content: [
          ...(message.content ? [{ type: 'input_text', text: message.content }] : []),
          ...message.images.map((image) => ({ type: 'input_image', image_url: image.dataUrl })),
        ],
      })
    } else {
      input.push({ role: message.role, content: message.content })
    }
  }
  flushImages()
  return input
}

const refusalsIn = (response) =>
  (response?.output ?? []).flatMap((item) => (item.content ?? []).filter((c) => c.type === 'refusal' && typeof c.refusal === 'string').map((c) => c.refusal))

export async function* parseResponsesStream(body) {
  const calls = new Map()
  let refusalStreamed = false
  for await (const payload of ssePayloads(body)) {
    let event
    try {
      event = JSON.parse(payload)
    } catch {
      continue
    }
    if (event.type === 'response.output_text.delta' && typeof event.delta === 'string') {
      yield { type: 'text', text: event.delta }
    } else if ((event.type === 'response.reasoning_summary_text.delta' || event.type === 'response.reasoning_text.delta') && typeof event.delta === 'string' && event.delta !== '') {
      yield { type: 'reasoning', text: event.delta }
    } else if (event.type === 'response.refusal.delta' && typeof event.delta === 'string' && event.delta !== '') {
      refusalStreamed = true
      yield { type: 'refusal', text: event.delta }
    } else if (event.type === 'response.output_item.added' && event.item?.type === 'function_call') {
      calls.set(event.item.id, { id: event.item.call_id ?? event.item.id, name: event.item.name ?? '', args: '' })
    } else if (event.type === 'response.function_call_arguments.delta') {
      const acc = calls.get(event.item_id) ?? { id: event.item_id, name: '', args: '' }
      acc.args += event.delta ?? ''
      calls.set(event.item_id, acc)
    } else if (event.type === 'response.completed') {
      // Meta's safety filter answers with refusal content in the completed output and no deltas.
      if (!refusalStreamed) for (const text of refusalsIn(event.response)) yield { type: 'refusal', text }
      const usage = event.response?.usage
      if (usage) {
        yield {
          type: 'usage',
          inputTokens: usage.input_tokens ?? null,
          outputTokens: usage.output_tokens ?? null,
          reasoningTokens: usage.output_tokens_details?.reasoning_tokens ?? null,
        }
      }
      break
    } else if (event.type === 'response.failed') {
      throw new Error(`responses: ${event.response?.error?.message ?? 'response failed'}`)
    } else if (event.type === 'response.incomplete') {
      throw new Error(`responses: incomplete (${event.response?.incomplete_details?.reason ?? 'unknown reason'})`)
    } else if (event.type === 'error') {
      throw new Error(`responses: ${event.message ?? 'provider error'}`)
    }
  }
  for (const acc of calls.values()) yield { type: 'tool_use', id: acc.id, name: acc.name, ...parseToolArguments(acc.args) }
  yield { type: 'done', stopReason: 'completed' }
}

// Provider configs that refused `reasoning.summary`, for the rest of the page session.
const refusedSummary = new Set()

const rejectsSummary = (err) => (err?.status === 400 || err?.status === 422) && typeof err.body === 'string' && /summary/i.test(err.body)

export const responsesProvider = (config) => {
  const sessionId = config.sessionId ?? crypto.randomUUID()
  const url = `${config.baseUrl ?? PROVIDER_BASE_URLS[config.kind]}/v1/responses`
  const configKey = `${config.kind}|${url}|${config.model}`
  return {
    async *send(messages, tools, { toolChoice, turnId, round } = {}) {
      const body = {
        model: config.model,
        stream: true,
        input: toResponsesInput(messages),
      }
      if (tools.length > 0) body.tools = tools.map(toResponsesTool)
      if (toolChoice === 'none') body.tool_choice = 'none'
      else if (toolChoice === 'any') body.tool_choice = 'required'
      const summary = Boolean(config.effort) && !refusedSummary.has(configKey)
      if (config.effort) body.reasoning = summary ? { effort: config.effort, summary: 'auto' } : { effort: config.effort }
      const headers = {
        'content-type': 'application/json',
        authorization: `Bearer ${config.apiKey}`,
        ...chatLogHeaders(config, { turnId, round }),
      }
      if (config.kind === 'opencode-go') headers['x-opencode-session'] = sessionId
      const post = (payload) => streamWithRetry('responses', url, { method: 'POST', headers, body: encodeBody(payload, config.maxBodyBytes) }, parseResponsesStream)
      try {
        yield* post(body)
      } catch (err) {
        if (!summary || !rejectsSummary(err)) throw err
        refusedSummary.add(configKey)
        yield* post({ ...body, reasoning: { effort: config.effort } })
      }
    },
  }
}
